import { Instance, SnapshotOut, types, flow, getRoot } from "mobx-state-tree"
import DeviceInfo from "~/ui/device-info"
import { navigate } from "~router/navigation"
import { RootStoreModel } from "~models/root-store"
import { withEnvironment } from "../extensions/with-environment"
import * as storage from "~utils/storage"
import {
  SECURE_STORAGE_KEY_TOKEN,
  HAVE_BEEN_PASS_PRIVACY,
  HAVE_BEEN_PASS_INTRO,
  STORAGE_KEY_TOKEN_FALLBACK,
  NAVIGATION_PERSISTENCE_KEY,
  USER_STATE_STORAGE_KEY,
} from "~utils/storage/storage-keys"
import { secureStorage } from "~utils/secureStorage"
import { normalizeAuthSession } from "~utils/auth-session"
/**
 * AuthStore 认证令牌存储
 */
export const AuthStoreModel = types
  .model("AuthStore")
  .props({
    token: types.optional(types.string, ""), // 认证成功后的登录令牌
    deviceId: types.optional(types.string, ""),
    deviceName: types.optional(types.string, ""),
    clientId: types.optional(types.string, ""), // 推送 registration id
    haveBeenPassIntro: types.optional(types.boolean, false),
    haveBeenPassPrivacy: types.optional(types.boolean, false),
    haveBeenPassEmailCheck: types.optional(types.boolean, false),
    userID: types.maybeNull(types.number),
  })
  .extend(withEnvironment)
  .views((self) => ({
    get hasToken() {
      return !!self.token
    },
    get getToken() {
      return self.token
    },
    get initialRouteName() {
      return self.haveBeenPassIntro ? "Login" : "AppIntro"
    },
  }))
  .actions((self) => {
    const root = () => getRoot<Instance<typeof RootStoreModel>>(self)

    const setToken = flow(function* (t: string) {
      self.token = t
      try {
        const available = yield secureStorage.isAvailableAsync()
        if (available) {
          if (t) {
            yield secureStorage.setItemAsync(SECURE_STORAGE_KEY_TOKEN, t)
          } else {
            yield secureStorage.deleteItemAsync(SECURE_STORAGE_KEY_TOKEN)
          }
        }
      } catch {
        // ignore
      }
      // Keep fallback always in sync so logout cannot be "undone" on next boot.
      yield storage.saveString(STORAGE_KEY_TOKEN_FALLBACK, t || "")
    })
    const login = flow(function* (payload: unknown) {
      const session = normalizeAuthSession(payload)
      if (!session.token) {
        throw new Error("登录令牌无效")
      }

      const { userStore } = root()
      if (session.user) {
        const curUser = userStore.addUser(session.user)
        userStore.setCurrentUser(curUser)
        self.userID = session.user.id ?? session.user.user_id ?? null
      }

      yield setToken(session.token)
      root().walletStore.resetRedPacketFormPayPwdSession()
      try {
        yield root().notificationStore.refreshUserUnreadNotificationCount()
      } catch {
        // 登录后通知拉取失败不阻断跳转
      }
      yield storage.remove(NAVIGATION_PERSISTENCE_KEY)
      navigate("ChatList")
    })

    const clearUser = flow(function* () {
      yield setToken("")
      try {
        yield secureStorage.deleteItemAsync(SECURE_STORAGE_KEY_TOKEN)
      } catch {
        // ignore
      }
      yield storage.remove(STORAGE_KEY_TOKEN_FALLBACK)
      yield storage.remove(NAVIGATION_PERSISTENCE_KEY)
      // 关键：立刻清掉持久化快照，避免“登出后刷新又被旧 token 回填”
      yield storage.remove(USER_STATE_STORAGE_KEY)
      navigate("Login")
      self.userID = null
      root().setSyncFinished(false)
      root().notificationStore.resetUserUnreadNotificationCount()
      root().walletStore.resetRedPacketFormPayPwdSession()
    })

    // 登出
    const logout = flow(function* () {
      yield clearUser()
      root().websocketStore.close()
      setTimeout(() => {
        root().reset()
      }, 1000)
    })

    const setDeviceId = (id) => {
      self.deviceId = id
    }

     const setDeviceName = (text) => {
      self.deviceName = text
    }

    const setClientId = (cid: string) => {
      self.clientId = cid
    }
    const setHaveBeenPrivacy = (truthy: boolean) => {
      self.haveBeenPassPrivacy = truthy
      storage.save(HAVE_BEEN_PASS_PRIVACY, truthy)
    }
    const setHaveBeenPassIntro = (truthy: boolean) => {
      self.haveBeenPassIntro = truthy
      storage.save(HAVE_BEEN_PASS_INTRO, truthy)
    }
    const setHaveBeenPassEmailCheck = (truthy: boolean) => {
      self.haveBeenPassEmailCheck = truthy
    }

    return {
      login,
      logout,
      clearUser,
      setDeviceId,
      setDeviceName,
      setClientId,
      setHaveBeenPrivacy,
      setHaveBeenPassIntro,
      setHaveBeenPassEmailCheck,
      setToken,
    }
  }).actions((self) => ({
    afterCreate: flow(function* () {
        try {
          // 优先用 expo-device 的 unique id
          const id = yield DeviceInfo.getUniqueId();
           
          if (id) {
            self.deviceId = id
          }
          const deviceName = yield (DeviceInfo.getLoginDeviceName?.() ?? DeviceInfo.getDeviceName())
          self.deviceName = deviceName
        } catch (e) {
          // console.warn("Failed to load device ID", e)
        }
      })
  }))

type AuthStoreType = Instance<typeof AuthStoreModel>
export interface AuthStore extends AuthStoreType {}
type AuthStoreSnapshotType = SnapshotOut<typeof AuthStoreModel>
export interface AuthStoreSnapshot extends AuthStoreSnapshotType {}
export const createAuthStoreDefaultModel = () => types.optional(AuthStoreModel, {})
